import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    PhoneVerificationYClientsState,
    PhoneVerificationStateValue,
    PhoneVerificationStatus,
} from '@target-web/leadads-form-view';
import {PhoneVerificationUserInfo} from 'api/types/PhoneVerification';
import {sendYClientsVerifyCodeRequest} from 'api/services/leads';
import {useUserInfo} from 'utils/userInfoContext';
import {useSearchParams} from 'utils/searchParamsContext';
import {isDev, VERIFICATION_PHONE_SEND_LIMIT_IN_MS, VERIFICATION_SIGN_UP_LIMIT_IN_MS} from 'utils/constants';
import {SendLeadYClientsResponse} from 'utils/types';
import {getUserPhoneDataDecodeParams} from 'utils/utils';

type AntifraudTimers = {
    send: ReturnType<typeof setTimeout> | null;
    attempt: ReturnType<typeof setTimeout> | null;
};

type PrevPhoneState = {
    phone: string;
};

function usePhoneVerificationYClientsController(
    codeLength: number,
    sendLead: (code: string) => Promise<SendLeadYClientsResponse>,
) {
    const defaultAntifraudTimers: AntifraudTimers = {
        send: null,
        attempt: null,
    };
    const prevPhoneState = useRef<PrevPhoneState | undefined>();
    const userInfo = useUserInfo();
    const {formDataHash} = useSearchParams();
    const [phoneVerificationState, setPhoneVerificationState] = useState<PhoneVerificationYClientsState>({
        status: PhoneVerificationStatus.unknown, // используется только для лоадера страницы
        attempt: {timestamp: undefined},
        antifraudExperies: {
            send: null,
            attempt: null,
        },
    });

    const [antifraudTimers, setAntifraudTimers] = useState<AntifraudTimers>(defaultAntifraudTimers);

    const phoneData = useMemo((): PhoneVerificationUserInfo | null => {
        if (!phoneVerificationState.phone) return null;

        const data = getUserPhoneDataDecodeParams(userInfo);

        return {
            ...data,
            phone: phoneVerificationState.phone.value,
            ...(phoneVerificationState.phone.hash ? {phone_hash: phoneVerificationState.phone.hash} : {}),
        };
    }, [phoneVerificationState, userInfo]);

    // метод инициализации
    const setPhoneForCheckingVerificationStatus = useCallback(
        (phone: PhoneVerificationStateValue) => {
            if (!phone.value) return;

            prevPhoneState.current = {
                phone: phoneVerificationState.phone?.value || '',
            };

            setPhoneVerificationState((prev) => ({
                ...prev,
                phone,
                code: undefined,
                errors: undefined,
            }));
        },
        [phoneVerificationState.phone?.value],
    );

    const sendCode = useCallback(async () => {
        if (!phoneData) return;

        // отправляем код
        try {
            await sendYClientsVerifyCodeRequest(formDataHash, phoneData);
            // #TODO выяснить и доработать ошибки бана и прочего
        } catch (err) {
            if (isDev) console.log(err);
        }

        // и выставляем timestamp для следующей отправки
        setPhoneVerificationState((prev) => ({
            ...prev,
            antifraudExperies: {
                ...prev.antifraudExperies,
                send: Date.now(), // метка последней отправки кода
            },
        }));
    }, [formDataHash, phoneData]);

    useEffect(() => {
        // при изменении телефона заново пошлем код
        if (
            phoneVerificationState.phone?.value &&
            prevPhoneState.current?.phone !== phoneVerificationState.phone?.value
        ) {
            sendCode();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phoneVerificationState.phone?.value]);

    const setCodeForVerify = useCallback(
        (code: string) => {
            if (code.length !== codeLength) return false;
            setPhoneVerificationState((prev) => ({
                ...prev,
                errors: undefined,
                code: {value: code, hash: `${code}_${Date.now().toString()}`},
            }));
            return true;
        },
        [codeLength],
    );

    // тут отсылаем введенный код
    const _attemptVerifyCode = useCallback(async (): Promise<void> => {
        if (!phoneVerificationState.code || !phoneVerificationState.phone || !phoneData) return;

        setPhoneVerificationState((prev) => ({
            ...prev,
            status: PhoneVerificationStatus.verificationProcessing,
        }));

        const timestamp = Date.now();
        if (
            phoneVerificationState.antifraudExperies.attempt &&
            phoneVerificationState.antifraudExperies.attempt > timestamp
        ) {
            if (antifraudTimers.attempt) return;
            const timer = setTimeout(
                () => setAntifraudTimers((prev) => ({...prev, attempt: null})),
                phoneVerificationState.antifraudExperies.attempt - timestamp,
            );
            setAntifraudTimers((prev) => ({...prev, attempt: timer}));
            return;
        }

        try {
            const response = await sendLead(phoneVerificationState.code.value);
            if (!response) {
                throw new Error('Bad request');
            }

            if (!response.status) {
                // неверный код или что-то еще
                setPhoneVerificationState((prev) => ({
                    ...prev,
                    errors: response.message ?? 'Что-то пошло не так :-(',
                    antifraudExperies: {
                        ...prev.antifraudExperies,
                        attempt: Date.now() + VERIFICATION_SIGN_UP_LIMIT_IN_MS, // todo поправить лимит
                    },
                }));
            }
        } catch (err) {
            console.error(err);
            return;
        } finally {
            setPhoneVerificationState((prev) => ({
                ...prev,
                status: PhoneVerificationStatus.unknown,
                attempt: {
                    ...prev.attempt,
                    timestamp: Date.now(),
                },
            }));
        }
    }, [
        phoneData,
        phoneVerificationState.code,
        phoneVerificationState.phone,
        phoneVerificationState.antifraudExperies.attempt,
        antifraudTimers.attempt,
        sendLead,
    ]);

    useEffect(() => {
        // если изменили код (полные 4 цифры)
        _attemptVerifyCode();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phoneVerificationState.code?.hash]);

    useEffect(() => {
        if (antifraudTimers.attempt) return;
        // если завершился таймаут на отправку формы, то попробуем переотправить форму
        _attemptVerifyCode();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [antifraudTimers.attempt]);

    const changePhone = useCallback(() => {
        prevPhoneState.current = {
            phone: phoneVerificationState.phone?.value || '',
        };

        setPhoneVerificationState((prev) => ({
            ...prev,
            code: undefined,
            errors: undefined,
        }));
    }, [setPhoneVerificationState, phoneVerificationState]);

    const phone = useMemo(() => phoneVerificationState.phone?.value ?? '', [phoneVerificationState.phone?.value]);

    // остаток времени до возможности повторного запроса кода
    const [timeToResend, setTimeToResend] = useState<number>(0);

    const resendCode = useCallback(() => {
        if (timeToResend) return;
        sendCode();
    }, [sendCode, timeToResend]);

    // интервал обратного отсчета для повторной отправки кода
    const intervalId = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        // если изменилась метка последней отправки кода - запустим таймер обратного отсчета
        clearInterval(intervalId.current);

        if (!phoneVerificationState.antifraudExperies.send) return;

        setTimeToResend(VERIFICATION_PHONE_SEND_LIMIT_IN_MS / 1000);

        intervalId.current = setInterval(() => {
            if (!phoneVerificationState.antifraudExperies.send) return;
            const diff =
                VERIFICATION_PHONE_SEND_LIMIT_IN_MS + phoneVerificationState.antifraudExperies.send - Date.now();

            const newTime = Math.trunc(Math.max(0, diff) / 1000);

            setTimeToResend(newTime);

            if (!newTime) {
                clearInterval(intervalId.current);
            }
        }, 100);
    }, [phoneVerificationState.antifraudExperies.send]);

    return {
        timeToResend,
        phone,
        resendCode,

        codeLength,
        phoneVerificationState,
        changePhone,
        setCodeForVerify,
        setPhoneForCheckingVerificationStatus,
    };
}

export default usePhoneVerificationYClientsController;
